import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

/**
 * RulesTable Component
 *
 * Displays rules in a paginated DataTable with actions
 *
 * @component
 */
const RulesTable = ({ 
  rules = [], 
  loading = false, 
  onEdit, 
  onDelete, 
  onToggleStatus 
}) => {
  // Get teams from Redux (no color/severity)
  const teams = useSelector(state => state.dataCategorization.teams);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Team column template (no color/severity)
   */
  const teamBodyTemplate = (rowData) => {
    return <span>{rowData.team}</span>;
  };

  /**
   * Status column template
   */
  const statusBodyTemplate = (rowData) => (
    <Tag
      value={rowData.enabled ? 'Enabled' : 'Disabled'}
      severity={rowData.enabled ? 'success' : 'danger'}
      icon={rowData.enabled ? 'pi pi-check' : 'pi pi-times'}
    />
  );

  /**
   * Conditions count template
   */
  const conditionsBodyTemplate = (rowData) => (
    <span>
      <i className="pi pi-list" style={{ marginRight: '5px' }}></i>
      {rowData.conditions?.length || 0} condition(s)
    </span>
  );

  /**
   * Date column template
   */
  const dateBodyTemplate = (rowData) => (
    <span>{formatDate(rowData.createdAt)}</span>
  );

  /**
   * Actions column template
   */
  const actionsBodyTemplate = (rowData) => (
    <div className="actions-buttons">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text p-button-info"
        onClick={() => onEdit(rowData)}
        tooltip="Edit Rule"
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        icon={rowData.enabled ? 'pi pi-times-circle' : 'pi pi-check-circle'}
        className="p-button-rounded p-button-text p-button-warning"
        onClick={() => onToggleStatus(rowData.ruleId, rowData.enabled)}
        tooltip={rowData.enabled ? 'Disable Rule' : 'Enable Rule'}
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger"
        onClick={() => onDelete(rowData.ruleId)}
        tooltip="Delete Rule"
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  );

  return (
    <div className="rules-table-container">
      <DataTable
        value={rules || []}
        loading={loading}
        paginator
        rows={20}
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage="No rules found. Create your first rule to get started."
        className="rules-table"
        stripedRows
        responsiveLayout="scroll"
      >
        <Column
          field="name"
          header="Rule Name"
          sortable
          style={{ minWidth: '200px' }}
        />
        <Column
          field="team"
          header="Team"
          body={teamBodyTemplate}
          sortable
          style={{ minWidth: '150px' }}
        />
        <Column
          field="targetCategory"
          header="Target Category"
          sortable
          style={{ minWidth: '200px' }}
        />
        <Column
          header="Conditions"
          body={conditionsBodyTemplate}
          style={{ minWidth: '150px' }}
        />
        <Column
          field="enabled"
          header="Status"
          body={statusBodyTemplate}
          sortable
          style={{ minWidth: '120px' }}
        />
        <Column
          field="createdAt"
          header="Created"
          body={dateBodyTemplate}
          sortable
          style={{ minWidth: '180px' }}
        />
        <Column
          body={actionsBodyTemplate}
          header="Actions"
          exportable={false}
          style={{ minWidth: '150px', textAlign: 'center' }}
        />
      </DataTable>
    </div>
  );
};

RulesTable.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired
};

export default RulesTable;

